import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Thermometer, MapPin, Star, TrendingUp, Users, Target, 
  CheckCircle, ArrowRight, Phone, Calendar, Wrench,
  Clock, Shield, BarChart3, Zap, Download, FileText
} from "lucide-react";

const HVACMarketingPage = () => {

  useEffect(() => {
    document.title = "HVAC Marketing Dallas | AC & Heating Company SEO | Digital Media Ninja";
  }, []);

  const stats = [
    { value: "Leads", label: "Seasonal Demand Capture", icon: TrendingUp },
    { value: "HVAC", label: "Industry Specialists", icon: Wrench },
    { value: "CPL", label: "Cost Optimization", icon: Target },
    { value: "Growth", label: "Revenue-Focused Plans", icon: BarChart3 }
  ];

  const services = [
    { icon: MapPin, title: "Local SEO for HVAC", desc: "Rank #1 for 'AC repair near me' and emergency heating searches in Dallas-Fort Worth." },
    { icon: Zap, title: "Google Local Service Ads", desc: "Get the Google Guaranteed badge and appear at the top of local searches." },
    { icon: Clock, title: "24/7 Emergency Campaigns", desc: "Capture high-intent emergency AC and heating repair searches around the clock." },
    { icon: Star, title: "Review Management", desc: "Build a 5-star reputation that converts browsers into customers." },
    { icon: Shield, title: "Service Area Pages", desc: "Dominate search results in every city and neighborhood you serve." },
    { icon: BarChart3, title: "Call Tracking & ROI", desc: "Track every lead source and prove marketing ROI with detailed analytics." }
  ];

  const serviceAreas = [
    "Dallas", "Fort Worth", "Plano", "Irving", "Arlington", 
    "Frisco", "McKinney", "Denton", "Richardson", "Garland",
    "Mesquite", "Carrollton", "Lewisville", "Allen"
  ];

  const faqs = [
    { 
      q: "How do I get more emergency HVAC calls?", 
      a: "Emergency HVAC calls come from ranking high in local search for terms like 'AC repair near me' and 'emergency heating repair Dallas.' We optimize your Google Business Profile, create location-specific landing pages, and run Google Ads targeting these high-intent searches. Results vary by market and competition — we track every call and lead so you can see exactly what your campaigns produce." 
    },
    { 
      q: "What's the best marketing strategy for seasonal HVAC demand?", 
      a: "We recommend a multi-channel approach: ramp up PPC spend 4-6 weeks before peak seasons (summer for AC, winter for heating), send email campaigns to your customer database for tune-up reminders, create seasonal content for SEO, and run social media promotions. This ensures you capture demand when it peaks." 
    },
    { 
      q: "How important are reviews for HVAC companies?", 
      a: "Reviews are critical for HVAC companies—88% of consumers trust online reviews as much as personal recommendations. We implement automated review request systems, train your team on asking for reviews, and help you respond professionally to all feedback. Review counts grow steadily once a consistent ask-and-respond system is in place." 
    },
    { 
      q: "What's the average cost per lead for HVAC marketing?", 
      a: "HVAC cost per lead varies by service type: Emergency repairs typically cost $50-150/lead via Google Ads, maintenance/tune-ups run $30-80/lead, and new installations can range from $100-300/lead. SEO generates leads at $15-40/lead over time. We continuously optimize campaigns to lower your cost per lead." 
    }
  ];

  // Schema markup for SEO
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "HVAC Marketing & SEO Services Dallas",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Digital Media Ninja",
          "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX", "addressCountry": "US" },
          "telephone": "(432) 934-9470"
        },
        "areaServed": serviceAreas.map(city => ({ "@type": "City", "name": city })),
        "description": "Specialized digital marketing for HVAC contractors in Dallas-Fort Worth. Local SEO, Google Ads, review management, and lead generation for AC repair and heating companies.",
        "serviceType": "HVAC Digital Marketing"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@type": "WebPage",
        "name": "HVAC Marketing Dallas | AC & Heating Company SEO",
        "description": "Expert HVAC marketing and SEO services in Dallas-Fort Worth. Generate more AC repair and heating service calls.",
        "url": "https://digitalmedianinja.com/hvac-marketing",
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".faq-answer"]
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black" data-testid="hvac-marketing-page">
      <Helmet>
        <title>HVAC Marketing Dallas | AC & Heating Company SEO | Digital Media Ninja</title>
        <meta name="description" content="Expert HVAC marketing and SEO services in Dallas-Fort Worth. Generate more AC repair and heating service calls with local SEO, Google Ads, and reputation management." />
        <meta name="keywords" content="HVAC marketing Dallas, AC repair SEO, heating company marketing, HVAC SEO Dallas, air conditioning marketing DFW, HVAC lead generation, Google Ads for HVAC, HVAC digital marketing" />
        <link rel="canonical" href="https://digitalmedianinja.com/hvac-marketing" />
        <meta property="og:title" content="HVAC Marketing Dallas | AC & Heating Company SEO" />
        <meta property="og:description" content="Expert HVAC marketing for Dallas contractors. Local SEO, Google Ads, review management." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG" />
        <meta property="og:url" content="https://digitalmedianinja.com/hvac-marketing" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-red-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
              <Thermometer className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 text-sm font-medium">HVAC Industry Specialists</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Generate More <span className="text-orange-400">HVAC Leads</span> in Dallas-Fort Worth
            </h1>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Dominate local search for AC repair, heating services, and HVAC installation. We help Dallas contractors capture emergency calls and grow their business year-round.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-12">
              <BookConsultationButton meetingType="hvac" size="lg">
                Get Free HVAC Marketing Audit <ArrowRight className="w-5 h-5 ml-2" />
              </BookConsultationButton>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-orange-400/30 text-orange-400 hover:bg-orange-400/10"
                onClick={() => window.location.href='/blog/how-to-market-an-hvac-business'}
                data-testid="read-blog-btn"
              >
                <FileText className="w-5 h-5 mr-2" /> Read Our Full Guide
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href='/guides/hvac-marketing'}
                data-testid="read-playbook-btn"
              >
                <Download className="w-5 h-5 mr-2" /> Marketing Playbook
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <stat.icon className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">HVAC Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing designed specifically for heating and cooling contractors.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-orange-400/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-orange-400/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">DFW Service Areas We Cover</h2>
          <p className="text-gray-400 text-center mb-12">We help HVAC companies dominate search in every city across Dallas-Fort Worth.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {serviceAreas.map((area, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-xl border border-white/10 text-center hover:border-orange-400/30 transition-colors">
                <p className="text-white font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">HVAC Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-orange-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400 faq-answer">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-red-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Grow Your HVAC Business?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">Get a free HVAC marketing audit and custom strategy for your Dallas-Fort Worth service area.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <BookConsultationButton meetingType="hvac" size="lg">
              <Calendar className="w-5 h-5 mr-2" /> Book Strategy Call
            </BookConsultationButton>
            <a href="tel:+14329349470">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Call (432) 934-9470
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HVACMarketingPage;
